import { apiPost, apiPostSession } from '../client';
import { LONG_TIMEOUT_MS } from '../../config/env';
import type { ResponseBase } from '../types/common';
import type {
  BookingRequest,
  BusAllocateRequest,
  BusCartResponse,
  BusLocationOption,
  BusLocationRecord,
  BusLocationSearchRequest,
  BusLocationSearchResponse,
  BusSchemaRequest,
  BusSchemaResponse,
  BusSearchRequest,
  BusSearchResponse,
  GetWithQueryRequest,
  PaymentRequest,
  PaymentResponse,
  PrebookingRequest,
  UpdatePassengersRequest,
} from '../types/bus';

/** `Bus/Location/Search` — provider-side terminal search (calls each provider's
 *  `IBusLocationSearchable`). Wired into `BusLocationAutocomplete` as an optional
 *  provider-backed alternative source; the primary source stays `getBusLocationsWithQuery`
 *  (local `BusLocations` table), which returns real data in every environment tried. */
export function searchBusLocations(location: string): Promise<BusLocationSearchResponse> {
  return apiPost<BusLocationSearchRequest, BusLocationSearchResponse>('Bus/Location/Search', { location });
}

/** `BusLocation/Get` — full stored terminal list. Body is a bare `SessionHeader`
 *  (`apiPostSession`); the response is a BARE ARRAY, not ResponseBase-wrapped, so we cast
 *  like `getBusLocationsWithQuery` does. Used as the client-side full-list fallback in the
 *  terminal autocomplete. */
export async function getBusLocations(): Promise<BusLocationRecord[]> {
  const response = await apiPostSession<ResponseBase>('BusLocation/Get');
  return (response as unknown as BusLocationRecord[]) ?? [];
}

/** Otomatik tamamlama — actual source for the search form's origin/destination fields.
 *  Confirmed via curl: response is a bare array, not ResponseBase-wrapped (see
 *  GetWithQueryRequest doc comment). apiPost requires `TRes extends ResponseBase`, so we
 *  satisfy that with the (unused) ResponseBase type param and cast the real array shape. */
export async function getBusLocationsWithQuery(query: string): Promise<BusLocationOption[]> {
  const response = await apiPost<GetWithQueryRequest, ResponseBase>('BusLocation/GetWithQuery', { query });
  return (response as unknown as BusLocationOption[]) ?? [];
}

export function searchBusTrips(body: BusSearchRequest): Promise<BusSearchResponse> {
  return apiPost<BusSearchRequest, BusSearchResponse>('Bus/Search', body);
}

export function getBusSchema(body: BusSchemaRequest): Promise<BusSchemaResponse> {
  return apiPost<BusSchemaRequest, BusSchemaResponse>('Bus/GetBusSchema', body);
}

export function allocateBus(body: BusAllocateRequest): Promise<BusCartResponse> {
  return apiPost<BusAllocateRequest, BusCartResponse>('Bus/Allocate', body);
}

export function updateBusPassengers(body: UpdatePassengersRequest): Promise<BusCartResponse> {
  return apiPost<UpdatePassengersRequest, BusCartResponse>('Bus/UpdatePassengers', body);
}

export function makeBusPreBooking(body: PrebookingRequest): Promise<BusCartResponse> {
  return apiPost<PrebookingRequest, BusCartResponse>('Bus/MakePreBooking', body);
}

/** Async-flagged in the migration guide — LONG_TIMEOUT_MS. */
export function finalizeBusShopping(body: BookingRequest): Promise<BusCartResponse> {
  return apiPost<BookingRequest, BusCartResponse>('Bus/FinalizeShopping', body, { timeoutMs: LONG_TIMEOUT_MS });
}

/** Async-flagged in the migration guide — LONG_TIMEOUT_MS. Shared (non-Bus-prefixed) route. */
export function makeBusPayment(body: PaymentRequest): Promise<PaymentResponse> {
  return apiPost<PaymentRequest, PaymentResponse>('MakePayment', body, { timeoutMs: LONG_TIMEOUT_MS });
}
